import { c as _c } from "react/compiler-runtime";
import React from "react";
import { cn } from "@/lib/utils";
import { SanityImage } from "@/components/atoms/SanityImage";
import type { SanityImageSource } from "@/components/atoms/SanityImage";
import { BackgroundPattern, type BackgroundPatternType } from "@/components/atoms/BackgroundPattern";
import { BackgroundVideo } from "./BackgroundVideo";
import { BackgroundVideoVidyard } from "./BackgroundVideoVidyard";
import { getSchemeGradientColors } from "@/lib/colors";
import { buildSanityImageUrl } from "@/lib/sanity-image";
import { Box } from "@/components/atoms/Box";
import type { SanityVideoData } from "./SectionBackground.props";
const POSTER_WIDTHS = [640, 828, 1080, 1200, 1920] as const;
function buildPosterUrl(poster: SanityImageSource | null | undefined): string {
  if (!poster?.asset?._id) return '';
  return buildSanityImageUrl(poster as Parameters<typeof buildSanityImageUrl>[0], {
    width: 1920,
    quality: 80
  });
}
function buildPosterSrcSet(poster: SanityImageSource | null | undefined): string | undefined {
  if (!poster?.asset?._id) return undefined;
  return POSTER_WIDTHS.map(w => `${buildSanityImageUrl(poster as Parameters<typeof buildSanityImageUrl>[0], {
    width: w,
    quality: 80
  })} ${w}w`).join(', ');
}
export type BackgroundLayersProps = {
  backgroundColor?: string;
  backgroundImage?: SanityImageSource;
  backgroundVideo?: SanityVideoData;
  backgroundPattern?: BackgroundPatternType;
  patternOpacity?: number | null;
  isDark?: boolean;
  className?: string;
  masked?: boolean;
  /** Enable priority loading for above-the-fold hero sections */
  priority?: boolean;
};
export const BackgroundLayers = t0 => {
  const $ = _c(19);
  const {
    backgroundColor: t1,
    backgroundImage,
    backgroundVideo,
    backgroundPattern,
    patternOpacity,
    isDark: t2,
    className,
    masked,
    priority: t3
  } = t0;
  const backgroundColor = t1 === undefined ? "white" : t1;
  const isDark = t2 === undefined ? false : t2;
  const priority = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== backgroundColor || $[1] !== backgroundPattern) {
    t4 = backgroundPattern === "organic-gradient" ? getSchemeGradientColors(backgroundColor) : undefined;
    $[0] = backgroundColor;
    $[1] = backgroundPattern;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const gradientColors = t4;
  let t5;
  if ($[3] !== className) {
    t5 = cn("not-prose absolute inset-0 z-0 pointer-events-none", className);
    $[3] = className;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== backgroundImage || $[6] !== backgroundVideo || $[7] !== priority) {
    t6 = backgroundVideo?.vidyardId ? <BackgroundVideoVidyard vidyardId={backgroundVideo.vidyardId} posterUrl={buildPosterUrl(backgroundVideo.poster)} posterSrcSet={buildPosterSrcSet(backgroundVideo.poster)} posterSizes="100vw" priority={priority} width={backgroundVideo.width || undefined} height={backgroundVideo.height || undefined} /> : backgroundVideo?.file?.asset?.url ? <BackgroundVideo videoUrl={backgroundVideo.file.asset.url} posterUrl={buildPosterUrl(backgroundVideo.poster)} posterSrcSet={buildPosterSrcSet(backgroundVideo.poster)} posterSizes="100vw" priority={priority} width={backgroundVideo.width || undefined} height={backgroundVideo.height || undefined} /> : backgroundImage ? <Box className="absolute inset-0 overflow-hidden"><SanityImage image={backgroundImage} className="size-full object-cover" fill={true} priority={priority} /></Box> : null;
    $[5] = backgroundImage;
    $[6] = backgroundVideo;
    $[7] = priority;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== backgroundPattern || $[10] !== gradientColors || $[11] !== isDark || $[12] !== masked || $[13] !== patternOpacity) {
    t7 = backgroundPattern && backgroundPattern !== "none" && <BackgroundPattern type={backgroundPattern as BackgroundPatternType} opacity={patternOpacity} isDark={isDark} masked={masked} gradientColors={gradientColors} />;
    $[9] = backgroundPattern;
    $[10] = gradientColors;
    $[11] = isDark;
    $[12] = masked;
    $[13] = patternOpacity;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <Box className={t5}>{t6}{t7}</Box>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};